import React, { useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { LuraLogo } from './LuraLogo'
import { BubbleBackground } from './BubbleBackground'

const getHumaneErrorMessage = (error: string, isSignUp: boolean): string => {
  const lowerError = error.toLowerCase()

  // Platform access errors - return as-is since they're already user-friendly
  if (
    lowerError.includes('healthcare providers only') ||
    lowerError.includes('patients only')
  ) {
    return error
  }

  // Sign in specific errors
  if (!isSignUp) {
    if (
      lowerError.includes('invalid login credentials') ||
      lowerError.includes('invalid credentials') ||
      lowerError.includes('wrong email or password')
    ) {
      return "We couldn't find an account with that email and password combination. Please check your details and try again."
    }

    if (
      lowerError.includes('too many requests') ||
      lowerError.includes('rate limit')
    ) {
      return 'Too many sign in attempts. Please wait a few minutes before trying again.'
    }

    if (
      lowerError.includes('user not found') ||
      lowerError.includes('account not found')
    ) {
      return "We couldn't find an account with that email address. Would you like to create a new account?"
    }
  }

  // Sign up specific errors
  if (isSignUp) {
    if (
      lowerError.includes('user already registered') ||
      lowerError.includes('email already registered') ||
      lowerError.includes('already exists')
    ) {
      return 'An account with this email already exists. Try signing in instead, or use a different email address.'
    }

    if (lowerError.includes('password') && lowerError.includes('weak')) {
      return 'Please choose a stronger password. Try using a mix of letters, numbers, and symbols.'
    }

    if (
      lowerError.includes('invalid email') ||
      lowerError.includes('email format')
    ) {
      return 'Please enter a valid email address.'
    }

    if (lowerError.includes('password') && lowerError.includes('short')) {
      return 'Password is too short. Please use at least 6 characters.'
    }
  }

  // General errors
  if (
    lowerError.includes('network') ||
    lowerError.includes('connection') ||
    lowerError.includes('timeout')
  ) {
    return 'Connection problem. Please check your internet connection and try again.'
  }

  if (
    lowerError.includes('server') ||
    lowerError.includes('service unavailable')
  ) {
    return 'Our service is temporarily unavailable. Please try again in a few minutes.'
  }

  // If no specific mapping found, return a generic friendly message
  return isSignUp
    ? "We couldn't create your account right now. Please try again or contact support if the problem continues."
    : "We couldn't sign you in right now. Please check your details and try again."
}

export const LoginForm: React.FC = () => {
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showUnconfirmed, setShowUnconfirmed] = useState(false)
  const [unconfirmedEmail, setUnconfirmedEmail] = useState('')
  const [showEmailConfirmed, setShowEmailConfirmed] = useState(false)
  const [hideLoginForm, setHideLoginForm] = useState(false)
  const [autoAuthInProgress, setAutoAuthInProgress] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [showResetEmailSent, setShowResetEmailSent] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const { signIn, signUp, resendConfirmation, resetPassword, user } = useAuth()
  const location = useLocation()

  useEffect(() => {
    // Check if we're on the email confirmation route: /login/tokenHash
    const pathParts = location.pathname.split('/')
    const isConfirmationRoute =
      pathParts.length === 3 &&
      pathParts[1] === 'login' &&
      pathParts[2] &&
      pathParts[2] !== 'email_confirmed'

    if (isConfirmationRoute) {
      const tokenHash = pathParts[2]
      setHideLoginForm(true)
      setAutoAuthInProgress(true)

      const verifyEmail = async () => {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'signup'
        })

        if (error) {
          console.error('Email verification failed:', error.message)
          setError('Email verification failed. The link may have expired. Please try signing in or request a new confirmation email.')
        } else {
          setShowEmailConfirmed(true)
        }

        setHideLoginForm(false)
        setAutoAuthInProgress(false)
        window.history.replaceState({}, document.title, '/login')
      }

      verifyEmail()
    }
  }, [location.pathname])

  if (user?.profile) {
    // Redirect to root route after login
    return <Navigate to="/" replace />
  }

  // Show loading screen during automatic authentication
  if (autoAuthInProgress) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-50 mb-4">
              <svg
                className="animate-spin h-6 w-6 text-primary-500"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Confirming your email...
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Please wait while we verify your account
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (showConfirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Check your email
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              We've sent a confirmation link to
            </p>
            <p className="mt-1 text-center text-sm font-medium text-gray-900">
              {email}
            </p>
            <p className="mt-4 text-center text-sm text-gray-600">
              Click the link in your email to activate your account. You may
              need to check your spam folder.
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  setShowConfirmation(false)
                  setIsSignUp(false)
                  setEmail('')
                  setPassword('')
                  setFirstName('')
                  setLastName('')
                  setError('')
                }}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <LuraLogo className="mb-4" />
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Forgot your password?
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your email address and we'll send you a link to reset your
              password.
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <form
            className="mt-8 space-y-6"
            onSubmit={async e => {
              e.preventDefault()
              if (!resetEmail) {
                setError('Please enter your email address')
                return
              }
              setLoading(true)
              setError('')
              try {
                await resetPassword(resetEmail)
                setShowForgotPassword(false)
                setShowResetEmailSent(true)
              } catch (err) {
                const errorMessage =
                  err instanceof Error
                    ? err.message
                    : 'Failed to send reset email'
                setError(errorMessage)
              } finally {
                setLoading(false)
              }
            }}
          >
            <div>
              <label htmlFor="reset-email" className="sr-only">
                Email address
              </label>
              <input
                id="reset-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-full text-true-black uppercase bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false)
                  setResetEmail('')
                  setError('')
                }}
                className="text-sm text-primary-600 hover:text-primary-500 font-bold"
              >
                Back to sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  if (showResetEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Check your email
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              We've sent a password reset link to
            </p>
            <p className="mt-1 text-center text-sm font-medium text-gray-900">
              {resetEmail}
            </p>
            <p className="mt-4 text-center text-sm text-gray-600">
              Click the link in your email to reset your password. You may need
              to check your spam folder.
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  setShowResetEmailSent(false)
                  setResetEmail('')
                }}
                className="text-sm text-primary-600 hover:text-primary-500 font-bold"
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (showUnconfirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
              <svg
                className="h-6 w-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Email not confirmed
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Your account exists but hasn't been confirmed yet.
            </p>
            <p className="mt-1 text-center text-sm font-medium text-gray-900">
              {unconfirmedEmail}
            </p>
            <p className="mt-4 text-center text-sm text-gray-600">
              Please check your email (including spam folder) for the
              confirmation link we sent when you signed up.
            </p>
            <div className="mt-6 space-y-4">
              <button
                onClick={async () => {
                  setLoading(true)
                  try {
                    await resendConfirmation(unconfirmedEmail)
                    setShowUnconfirmed(false)
                    setShowConfirmation(true)
                    setEmail(unconfirmedEmail)
                  } catch (err) {
                    const errorMessage =
                      err instanceof Error
                        ? err.message
                        : 'Failed to resend confirmation email'
                    setError(getHumaneErrorMessage(errorMessage, false))
                    setShowUnconfirmed(false)
                  } finally {
                    setLoading(false)
                  }
                }}
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send new confirmation email'}
              </button>
              <button
                onClick={() => {
                  setShowUnconfirmed(false)
                  setUnconfirmedEmail('')
                  setError('')
                }}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (isSignUp) {
        await signUp({
          email,
          password,
          first_name: firstName,
          last_name: lastName
        })
        setShowConfirmation(true)
      } else {
        await signIn(email, password)
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : `Failed to ${isSignUp ? 'sign up' : 'sign in'}`

      // Check if it's an unconfirmed email error during sign in
      if (
        !isSignUp &&
        (errorMessage.toLowerCase().includes('email not confirmed') ||
          errorMessage.toLowerCase().includes('signup confirmation required') ||
          errorMessage.toLowerCase().includes('email address not confirmed'))
      ) {
        setUnconfirmedEmail(email)
        setShowUnconfirmed(true)
        setError('')
      } else {
        setError(getHumaneErrorMessage(errorMessage, isSignUp))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <BubbleBackground>
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {!hideLoginForm && (
            <div>
              <LuraLogo className="mt-6" />
              <p className="mt-2 text-center text-sm text-soft-black font-bold">
                Healthcare Provider Portal
              </p>
            </div>
          )}
          {!hideLoginForm && (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              {showEmailConfirmed && (
                <div className="rounded-md bg-green-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-green-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">
                        Email confirmed successfully!
                      </p>
                      <p className="mt-1 text-sm text-green-700">
                        Your account is now active. You can sign in below.
                      </p>
                    </div>
                    <div className="ml-auto pl-3">
                      <div className="-mx-1.5 -my-1.5">
                        <button
                          type="button"
                          onClick={() => setShowEmailConfirmed(false)}
                          className="inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50"
                        >
                          <span className="sr-only">Dismiss</span>
                          <svg
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              )}

              <div className="rounded-md shadow-sm -space-y-px">
                {isSignUp && (
                  <>
                    <div>
                      <label htmlFor="firstName" className="sr-only">
                        First name
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                        placeholder="First name"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="sr-only">
                        Last name
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                        placeholder="Last name"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                      />
                    </div>
                  </>
                )}
                <div>
                  <label htmlFor="email" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm ${
                      isSignUp ? '' : 'rounded-t-md'
                    }`}
                    placeholder="Email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="sr-only">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete={
                      isSignUp ? 'new-password' : 'current-password'
                    }
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-full text-true-black uppercase bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? isSignUp
                      ? 'Creating account...'
                      : 'Signing in...'
                    : isSignUp
                      ? 'Create account'
                      : 'Sign in'}
                </button>
              </div>

              {!isSignUp && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary-600 hover:text-primary-500 font-bold"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="text-sm text-primary-600 hover:text-primary-500 font-bold"
                >
                  {isSignUp
                    ? 'Already have an account? Sign in'
                    : "Don't have an account? Sign up"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </BubbleBackground>
  )
}
